<template>
	<page-container class="index" :page="page">
		<heading-image v-if="page.headingImage" type="large">
			<hero-image>
				<div v-if="defaults[locale].homepage.headingImageTitle" class="hero-content">
					<h2 class="hero-title">
						{{ defaults[locale].homepage.headingImageTitle }}
					</h2>
					<div v-parse-links v-html="defaults[locale].homepage.headingImageContent" />
				</div>
				<picture>
					<source
						v-if="page.headingImagePortraitWebp"
						:srcset="page.headingImagePortraitWebp"
						media="(max-width: 500px)"
						type="image/webp"
					/>
					<source
						v-if="page.headingImagePortrait"
						:srcset="page.headingImagePortrait"
						media="(max-width: 500px)"
					/>
					<source :srcset="page.headingImageWebp" type="image/webp" />
					<source :srcset="page.headingImage" />
					<img :src="page.headingImage" :alt="page.headingImageAlt" />
				</picture>
			</hero-image>
		</heading-image>

		<main-content>
			<div class="main-intro columns column12">
				<div class="column6 intro-text">
					<h2 class="subtitle">{{ page.subtitle }}</h2>
					<h1>{{ page.title || page.header }}</h1>
					<div v-parse-links v-html="page.content" />
				</div>
				<div class="column6 intro-image">
					<picture>
						<source :srcset="defaults[locale].homepage.introImageWebp" type="image/webp" />
						<source :srcset="defaults[locale].homepage.introImage" />
						<img
							:src="defaults[locale].homepage.introImage"
							:alt="defaults[locale].homepage.introImageAlt"
							loading="lazy"
						/>
					</picture>
				</div>
			</div>
		</main-content>

		<promoblocks :items="promoblocksData" />

		<section v-if="defaults[locale].homepage.sectionReservationsTitle" class="section-reservations">
			<div class="row">
				<div class="columns column12">
					<div class="columns column5">
						<h2 class="subtitle">
							{{ defaults[locale].homepage.sectionReservationsSubtitle }}
						</h2>
						<h2>{{ defaults[locale].homepage.sectionReservationsTitle }}</h2>
						<div v-parse-links v-html="defaults[locale].homepage.sectionReservationsContent" />
					</div>
					<div class="columns column7">
						<div class="intro-image">
							<picture>
								<source
									:srcset="defaults[locale].homepage.sectionReservationsImageWebp"
									type="image/webp"
								/>
								<source :srcset="defaults[locale].homepage.sectionReservationsImageImage" />
								<img
									:src="defaults[locale].homepage.sectionReservationsImage"
									:alt="defaults[locale].homepage.sectionReservationsImageAlt"
									loading="lazy"
								/>
							</picture>
						</div>
					</div>
				</div>
			</div>
		</section>

		<section v-if="defaults[locale].homepage.promotionBackgroundImage" class="section-promotion">
			<div class="row">
				<div class="columns column12">
					<div class="columns column12">
						<picture>
							<source
								:srcset="defaults[locale].homepage.promotionBackgroundImageWebp"
								type="image/webp"
							/>
							<source :srcset="defaults[locale].homepage.promotionBackgroundImage" />
							<img class="promotions-image" :src="defaults[locale].homepage.promotionBackgroundImage" />
						</picture>
						<h2 class="subtitle">
							{{ defaults[locale].homepage.promotionsSubtitle }}
						</h2>
						<h2>{{ defaults[locale].homepage.promotionsHeader }}</h2>
						<div v-parse-links v-html="defaults[locale].homepage.promotionsContent" />
					</div>
				</div>
			</div>
		</section>

		<contentblocks :items="page.contentblocks" class="section-contentblocks">
			<template #blocks-header>
				<div class="row blocks-header align-center">
					<div class="columns column12">
						<h2 class="subtitle">
							{{ defaults[locale].homepage.sectionContentblocksSubtitle }}
						</h2>
						<h2>{{ defaults[locale].homepage.sectionContentblocksTitle }}</h2>
					</div>
				</div>
			</template>
		</contentblocks>

		<section v-if="defaults[locale].homepage.contactHeader" class="content-section section-contact contact-info">
			<div class="row">
				<div class="columns column12 align-center">
					<h2 class="subtitle">
						{{ defaults[locale].homepage.contactSubtitle }}
					</h2>
					<h2>
						{{ defaults[locale].homepage.contactHeader }}
					</h2>
					<div v-parse-links v-html="defaults[locale].homepage.contactContent" />
				</div>
			</div>
			<div class="row">
				<div class="columns column4">
					<p>
						<strong>{{ defaults[locale].website.restaurantName }}</strong>
						<br />
						<br />
						<br />
						<br />
						<span>{{ defaults[locale].website.street }} {{ defaults[locale].website.number }}</span>
						<br />
						<span>{{ defaults[locale].website.postalCode }} {{ defaults[locale].website.city }}</span>
						<br />
						<a :href="`tel:${$phoneRaw(defaults[locale].website.phone)}`">
							<font-awesome-icon class="icon" icon="fal fa-phone" size="1x" />
							{{ defaults[locale].website.phone }}
						</a>
						<br />
						<a :href="`mailto:${defaults[locale].website.email}`">
							<font-awesome-icon class="icon" icon="fal fa-envelope" size="1x" />
							{{ defaults[locale].website.email }}
						</a>
					</p>
				</div>
				<div class="columns column8">
					<iframe
						src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d30398.835755442353!2d4.863543627553277!3d52.37102190942485!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x47c609d34b945f3f%3A0xd8a48a8fbee1e61b!2sdinette!5e1!3m2!1sen!2snl!4v1744195887832!5m2!1sen!2snl"
						width="100%"
						height="500"
						frameborder="0"
						style="border: 0"
						allowfullscreen=""
						aria-hidden="false"
						tabindex="0"
					/>
				</div>
			</div>
		</section>

		<frequently-asked-questions
			v-if="page.frequentlyAskedQuestion"
			:items="page.frequentlyAskedQuestion.faqitems"
			:header="page.frequentlyAskedQuestion.header"
		/>
	</page-container>
</template>

<script setup>
const { page, fetchPage } = usePage();
const { defaults, fetchDefaults } = useDefaults();
const { locale } = useI18n();

await fetchPage();
await fetchDefaults();

const { data: promoblocksData } = await useWebsiteFetch('promoblocks', {
	query: { language: locale.value },
	key: `${locale.value}/promoblocks`,
});
</script>

<style lang="scss" scoped>
.hero-content {
	position: absolute;
	top: 50%;
	display: flex;
	flex-flow: column nowrap;
	align-items: center;
	color: #fff;
	max-width: 1280px;
	text-align: center;
	z-index: 5;
	left: 20px;
	right: 20px;
	margin: auto;
	transform: translateY(calc(-50% + 100px));
	animation: fade-from-bottom 1.5s ease;

	.hero-title {
		width: 100%;
		color: #fff;
		font-size: 56px;
		margin-bottom: 10px;
		line-height: normal;
	}

	:deep(> *) {
		max-width: 700px;
		margin: 0 0 10px;
	}
}

.main-intro {
	max-width: 1680px;
	margin: auto;
	display: flex;
	gap: 80px;

	.intro-text {
		padding-top: 70px;
		width: 100%;
		display: flex;
		flex-flow: column;
		justify-content: center;
		max-width: 530px;
	}

	.intro-image {
		width: 100%;
		display: flex;
		justify-content: center;
		align-items: center;
		overflow: hidden;
		position: relative;
	}

	.intro-image::before {
		content: '';
		display: block;
		padding-top: 100%; /* Enforces 1:1 aspect ratio */
	}

	.intro-image picture,
	.intro-image img {
		position: absolute;
		top: 0;
		left: 0;
		width: 100%;
		height: 100%;
		object-fit: cover;
	}
}

.section-reservations {
	margin-bottom: 24px;
	background-color: var(--primary-background-color);

	.row {
		.columns {
			display: flex;

			.columns {
				background-color: var(--tertiary-background-color);
			}

			.column5 {
				display: flex;
				flex-flow: column;
				justify-content: center;
				padding: 30px;
			}

			.intro-image {
				width: 100%;
				max-width: 900px;
				display: flex;
				justify-content: center;
				align-items: center;
				overflow: hidden;
				position: relative;
				margin: 30px 12px;
			}

			.intro-image::before {
				content: '';
				display: block;
				padding-top: 70%;
			}

			.intro-image picture,
			.intro-image img {
				position: absolute;
				top: 0;
				left: 0;
				width: 100%;
				height: 100%;
				object-fit: cover;
			}
		}
	}
}

.section-promotion {
	.row {
		position: relative;
		z-index: 10;
		color: var(--body-color);
		overflow: hidden;
		height: 600px;

		.column12 {
			position: relative;
			height: 100%;

			.column12 {
				height: 100%;
				display: flex;
				flex-flow: column;
				justify-content: center;
				align-items: center;
				padding: 0 20%;
				text-align: center;

				.subtitle,
				:deep(p) {
					color: white;
				}

				h2 {
					color: var(--tertiary-background-color);
				}

				picture,
				img {
					position: absolute;
					object-fit: cover;
					inset: 0;
					width: 100%;
					height: 100%;
					z-index: -1;

					&::after {
						background: rgb(0 0 0 / 30%);
						position: absolute;
						width: 100%;
						height: 100%;
						left: 0;
						top: 0;
						content: '';
					}
				}
			}
		}
	}

	h2:not(.subtitle) {
		color: var(--body-color);
	}
}

.section-contentblocks {
	max-width: 1680px;
	margin: 24px auto;
	padding: 24px 35px;

	.blocks-header {
		margin-bottom: 0;
		background-color: var(--secondary-background-color);
		height: 100%;
		padding: 54px 0;
	}
}

.section-contact {
	.row:first-child {
		padding-bottom: 50px;

		:deep(p) {
			display: inline;
			margin-right: 10px;
		}
	}

	.row {
		.column12 {
			:deep(strong) {
				font-weight: 300;
				color: var(--subtitle-color);
			}
		}

		.column4 {
			border-top: solid var(--cta-color) 1px;
			display: flex;
			flex-flow: column;
			justify-content: end;

			p {
				margin-bottom: 0;
			}

			strong {
				font-family: var(--heading-font-family);
				font-size: 32px;
				font-weight: 200;
				text-transform: uppercase;
				padding-bottom: 100px !important;
			}

			a {
				font-family: var(--body-font-family);

				.icon {
					margin-right: 5px;
				}
			}
		}
	}
}

@keyframes fade-from-bottom {
	0% {
		opacity: 0;
		transform: translateY(calc(-30% + 100px));
	}

	66% {
		opacity: 0;
	}

	100% {
		opacity: 1;
		transform: translateY(calc(-50% + 100px));
	}
}

@media (max-width: 1080px) {
	.hero-content {
		max-width: 650px;
	}

	.main-intro {
		flex-wrap: wrap;
	}
}

@media (max-width: 900px) {
	.section-contact {
		.row {
			.column4 {
				width: 100%;
				align-items: center;
				text-align: center;
				justify-content: end;
				padding: 40px 0;
			}
		}
	}
}

@media (max-width: 880px) {
	.section-promotions {
		padding: 125px 25px;
	}

	.promotions-image {
		position: absolute;
	}

	.hero-content {
		font-size: 15px;
		padding: 0 10px;

		.hero-title {
			font-size: 30px;
		}
	}

	.section-reservations {
		.row {
			.columns {
				width: 100%;
				flex-wrap: wrap;

				.column5 {
					padding: 24px 24px 0;
				}

				.column7 {
					.intro-image {
						margin: 0 12px 24px;
						padding: 0 10px;
					}
				}
			}
		}
	}

	.section-promotion {
		.row {
			height: 700px;

			.column12 {
				display: flex;
				justify-content: center;
				align-items: center;
				padding: 50px 0;
			}

			.promotions-image {
				position: relative;
				width: 100%;
				aspect-ratio: 1;
				object-fit: cover;
			}
		}
	}

	.section-contentblocks {
		padding: 24px 28px;
	}
}

@media (max-width: 600px) {
	.section-contact {
		display: none;
	}
}
</style>
